import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import ShareButtons from "@/components/ShareButtons";
import { blogAPI, seoCollectionAPI } from "@/lib/api";
import PageSEO from "@/components/PageSEO";
import GoogleSourceCTA from "@/components/GoogleSourceCTA";
import { Loader2, Calendar, Eye, Tag, ArrowLeft, Clock, User, Globe, TrendingUp } from "lucide-react";

// SEO helper function
const updateMetaTags = (title, description, canonicalUrl, image = null) => {
  document.title = title;
  
  const updateMeta = (selector, content, isProperty = false) => {
    let meta = document.querySelector(selector);
    if (meta) {
      meta.content = content;
    } else if (content) {
      meta = document.createElement('meta');
      if (isProperty) {
        meta.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
      } else {
        meta.name = selector.match(/name="([^"]+)"/)?.[1] || '';
      }
      meta.content = content;
      document.head.appendChild(meta);
    }
  };
  
  updateMeta('meta[name="description"]', description);
  updateMeta('meta[name="robots"]', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
  
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.href = canonicalUrl;
  
  updateMeta('meta[property="og:type"]', 'article', true);
  updateMeta('meta[property="og:url"]', canonicalUrl, true);
  updateMeta('meta[property="og:title"]', title, true);
  updateMeta('meta[property="og:description"]', description, true);
  if (image) updateMeta('meta[property="og:image"]', image, true);
  updateMeta('meta[property="og:site_name"]', 'Kokan Homestays', true);
  
  updateMeta('meta[name="twitter:card"]', 'summary_large_image');
  updateMeta('meta[name="twitter:title"]', title);
  updateMeta('meta[name="twitter:description"]', description);
  if (image) updateMeta('meta[name="twitter:image"]', image);
};

const BlogPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [seoCollections, setSeoCollections] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchPost();
    } else {
      fetchPosts();
      fetchSeoCollections();
    }
  }, [slug]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await blogAPI.getAll({ published_only: true });
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoCollections = async () => {
    try {
      const response = await seoCollectionAPI.getPublic();
      setSeoCollections(response.data.filter(c => c.show_in_blog));
    } catch {}
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await blogAPI.getBySlug(slug);
      setPost(response.data);
    } catch (error) {
      console.error("Failed to fetch post:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  // SEO: Update meta tags
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    if (slug && post) {
      // Single post SEO
      const featuredImage = post.featured_image?.startsWith('/') 
        ? `${BACKEND_URL}${post.featured_image}` 
        : post.featured_image;
      const postTitle = `${post.meta_title || post.title}`;
      const postDescription = post.meta_description || post.excerpt;
      const postUrl = `${window.location.origin}/blog/${post.slug}`;
      updateMetaTags(postTitle, postDescription, postUrl, featuredImage);
    } else if (!slug) {
      // Blog listing SEO
      updateMetaTags(
        'Blog | Kokan Homestays - Travel Tips & Guides',
        'Explore travel tips, destination guides, and homestay recommendations on the Kokan Homestays blog.',
        `${window.location.origin}/blog`
      );
    }
  }, [slug, post, BACKEND_URL]);

  // Process blog content - ensure proper HTML structure
  const processContent = (html) => {
    if (!html) return "";
    // If content already has block-level HTML, just return it
    if (/<(h[1-6]|ul|ol|table|blockquote|div|section)[\s>]/i.test(html)) {
      return html;
    }
    // Plain-text-like content: split by newlines, wrap in proper tags
    const lines = html.split(/\n/).filter((l) => l.trim());
    let result = "";
    let inList = false;
    for (const line of lines) {
      const trimmed = line.trim();
      // Detect list items (- or * or numbered)
      if (/^[-*]/.test(trimmed)) {
        if (!inList) { result += "<ul>"; inList = true; }
        result += `<li>${trimmed.replace(/^[-*]\s*/, "")}</li>`;
      } else if (/^\d+[.)]\s/.test(trimmed)) {
        if (!inList) { result += "<ol>"; inList = true; }
        result += `<li>${trimmed.replace(/^\d+[.)]\s*/, "")}</li>`;
      } else {
        if (inList) { result += inList ? "</ul>" : "</ol>"; inList = false; }
        // Detect headings (all bold text or short strong lines)
        if (/^<strong>.+<\/strong>$/.test(trimmed) && trimmed.length < 200) {
          result += `<h3>${trimmed.replace(/<\/?strong>/g, "")}</h3>`;
        } else {
          result += `<p>${trimmed}</p>`;
        }
      }
    }
    if (inList) result += "</ul>";
    return result;
  };

  // Single post view
  if (slug && post) {
    const featuredImage = post.featured_image?.startsWith('/') 
      ? `${BACKEND_URL}${post.featured_image}` 
      : post.featured_image;

    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="blog-post-page">
        <Navbar />
        {post && <PageSEO title={post.meta_title || post.title} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} image={post.featured_image} type="article" canonicalUrl={post.canonical_url} />}
        
        <article className="pt-24 pb-16">
          {/* Back Link */}
          <div className="max-w-4xl mx-auto px-6 mb-6">
            <Link to="/blog" className="inline-flex items-center text-primary hover:underline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </div>

          {/* Header */}
          <header className="max-w-4xl mx-auto px-6 mb-8">
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-medium text-foreground mb-6 leading-tight"
             
            >
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author || "Admin"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {calculateReadTime(post.content)} min read
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {post.views} views
              </span>
            </div>

            {/* Share Buttons */}
            <div className="mt-6 pt-4 border-t">
              <ShareButtons 
                url={`${window.location.origin}/blog/${post.slug}`}
                title={post.title}
                description={post.excerpt || post.meta_description}
                variant="inline"
              />
            </div>
          </header>

          {/* Featured Image */}
          {featuredImage && (
            <div className="max-w-5xl mx-auto px-6 mb-10">
              <div className="rounded-2xl overflow-hidden shadow-lg max-h-[480px]">
                <img
                  src={featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover max-h-[480px]"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="max-w-4xl mx-auto px-6">
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-semibold prose-headings:text-foreground prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-2xl prose-h2:border-b prose-h2:pb-2 prose-h2:border-border/40
                prose-h3:text-xl
                prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                prose-ul:my-4 prose-ul:pl-6 prose-ul:list-disc
                prose-ol:my-4 prose-ol:pl-6 prose-ol:list-decimal
                prose-li:text-muted-foreground prose-li:mb-2 prose-li:leading-relaxed
                prose-strong:text-foreground prose-strong:font-semibold
                prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground prose-blockquote:italic
                prose-img:rounded-xl prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: processContent(post.content) }}
            />

            {/* Author Box */}
            {post._author && (
              <div className="mt-10 pt-8 border-t" data-testid="author-box">
                <div className="flex items-start gap-5 bg-muted/30 rounded-2xl p-6">
                  {post._author.photo ? (
                    <img
                      src={post._author.photo}
                      alt={post._author.name}
                      className="w-16 h-16 rounded-full object-cover ring-2 ring-primary/20 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-semibold text-primary">
                        {post._author.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Written by</p>
                    <h4 className="text-lg font-semibold text-foreground">{post._author.name}</h4>
                    {post._author.bio && (
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{post._author.bio}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Share at bottom of post */}
            <div className="mt-10 pt-6 border-t">
              <p className="text-muted-foreground mb-3">Enjoyed this article? Share it with others!</p>
              <ShareButtons 
                url={`${window.location.origin}/blog/${post.slug}`}
                title={post.title}
                description={post.excerpt || post.meta_description}
                variant="inline"
              />
            </div>
          </div>
        </article>

        <Footer />
      </div>
    );
  }

  // Blog listing view
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="blog-listing-page">
      <Navbar />
      <PageSEO title="Blog" description="Read the latest travel tips, destination guides, and stories about Konkan coastal life." path="/blog" />

      {/* Hero */}
      <section className="pt-24 pb-12 px-6 md:px-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-7xl mx-auto text-center">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-medium text-foreground mb-4"
           
          >
            Our <span className="text-primary">Blog</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Travel tips, destination guides, and stories from our homestay community
          </p>
        </div>
      </section>

      {/* Popular Guides Strip */}
      {!loading && seoCollections.length > 1 && (
        <PopularGuidesWidget guides={seoCollections} />
      )}

      {/* Posts Grid */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : posts.length > 0 || seoCollections.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {seoCollections.map((col) => (
                <SeoCollectionCard key={`seo-${col.id}`} collection={col} />
              ))}
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <h3 className="text-2xl font-semibold text-foreground mb-2">
                No Blog Posts Yet
              </h3>
              <p className="text-muted-foreground">
                Check back soon for travel tips and destination guides!
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const BlogCard = ({ post }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const featuredImage = post.featured_image?.startsWith('/') 
    ? `${BACKEND_URL}${post.featured_image}` 
    : post.featured_image;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="bg-white rounded-xl shadow-soft overflow-hidden group hover:shadow-lg transition-all hover:-translate-y-1"
      data-testid={`blog-card-${post.id}`}
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        {featuredImage ? (
          <img
            src={featuredImage}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
            <span className="text-6xl opacity-50">📝</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {post.tags.slice(0, 2).map((tag, idx) => (
              <span key={idx} className="text-xs text-primary">
                #{tag}
              </span>
            ))}
          </div>
        )}

        <h3 className="font-semibold text-foreground text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {post.title}
        </h3>

        {post.excerpt && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{post.excerpt}</p>
        )}

        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-3 border-t">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(post.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {post.views} views
          </span>
        </div>
      </div>
    </Link>
  );
};

const SeoCollectionCard = ({ collection }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Link
      to={`/guide/${collection.slug}`}
      className="bg-white rounded-3xl overflow-hidden group hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
      data-testid={`seo-collection-card-${collection.id}`}
    >
      <div className="aspect-video bg-beach-sand/30 relative overflow-hidden">
        {collection.featured_image ? (
          <img src={collection.featured_image} alt={collection.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-coconut-green/10 to-beach-sand/30">
            <Globe className="w-14 h-14 text-coconut-green/30" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1.5 bg-sunset-gold text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-md">Guide</span>
        </div>
        {collection.place_ids?.length > 0 && (
          <span className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/40 backdrop-blur-sm text-white text-xs rounded-full font-medium">{collection.place_ids.length} places</span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-coconut-green transition-colors leading-snug">{collection.title}</h3>
        {collection.meta_description && <p className="text-sm text-gray-500 line-clamp-2 mb-4 leading-relaxed">{collection.meta_description}</p>}
        <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-beach-sand/40">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(collection.created_at)}</span>
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{collection.views || 0} views</span>
        </div>
      </div>
    </Link>
  );
};

const PopularGuidesWidget = ({ guides }) => {
  const sorted = [...guides].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 4);
  if (sorted.length === 0) return null;

  return (
    <section className="px-6 md:px-12 pb-4" data-testid="popular-guides-widget">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Most Read Guides</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin" style={{ scrollbarWidth: 'thin' }}>
          {sorted.map((g) => (
            <Link
              key={g.id}
              to={`/guide/${g.slug}`}
              className="flex-shrink-0 flex items-center gap-3 bg-white rounded-xl border border-border/30 px-4 py-3 hover:shadow-md hover:border-primary/20 transition-all group min-w-[280px] max-w-[340px]"
              data-testid={`popular-guide-${g.id}`}
            >
              {g.featured_image ? (
                <img src={g.featured_image} alt={g.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" loading="lazy" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-primary/30" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">{g.title}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{g.views || 0} views</span>
                  <span>{g.place_ids?.length || 0} places</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogPage;
